import { React, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  showModalPopup,
  resetAction,
  toggleSpinningLoaderAction,
  showRecordingSettingsPanel,
} from "../../../App/state/actions";
import { setRecordingAction, toggleSettingsPanelAction, EDIT_RECORDINGS } from "../../RecordingsPage/state/actions";
import recordingConstants from "../../../shared/constants/recordingConstants";
import { ANALYTICS_EVENT_TYPES, MAPPED_CONTENT_TYPES } from "../../../shared/constants/analytics";
import constants from "../../../shared/constants";
// TODO in future MVE: use RecordingModal when switching from design Option A to Option B
import RecordingModal from "../../../components/RecordingModal";
import { setRecording, getCPVRRecordingStatus } from "../../../shared/utils/recordingHelper";
import { trackGenericAction } from "../../../shared/analytics/dataLayer";

import {
  SET_RECORDINGS,
  MANIPULATE_RECORDING_ACTION_TRIGGERED,
  GET_RECORDINGS,
  DELETE_RECORDINGS,
  SET_TEAM_RECORDINGS,
} from "../../RecordingsPage/state/actions";
import { logDTEvent } from "../../../shared/analytics/dynatrace";
import { DT_EVENTS } from "../../../shared/constants/dynatrace";
import { useReducers } from "../../../shared/hooks/useReducer";

const { RECORDING_PARAMS, CPVR_INTERNAL_RECORDING_STATUS } = recordingConstants;
const { MODAL_TYPES, REDUCER_TYPE } = constants;

const useRecordingActions = (
  appProvider,
  isMR,
  getMediaNodeMetaData,
  selectedAssetRecordingInfo,
  setSelectedAssetRecordingInfo,
  setShowRecordingModal,
  isRecordingActionInProgress,
  setRecordingActionError,
  recordingEventTypeRef
) => {
  const dispatch = useDispatch();
  const { featureToggles } = useReducers(REDUCER_TYPE.APP);
  const { isDynatraceTrackingEnabled } = featureToggles;

  const showRecordingSettingHandler = useCallback(
    (recordingInfo, typeOfRecording) => {
      const isScheduleNeeded = !isMR;
      // Spinning loader implementation on the recordingSettingsPanel.
      isScheduleNeeded && dispatch(toggleSpinningLoaderAction(true, "loader-wrapper setting-spinner-loader"));
      dispatch(showRecordingSettingsPanel(typeOfRecording, recordingInfo, null, false, null, isScheduleNeeded));
      dispatch(toggleSettingsPanelAction(true));
    },
    [isMR, dispatch]
  );

  /**
   * @param {Object} recordingInfo - Object containing response of particular asset from recordingsList object to compare and edit the recordings.
   * @param {String} typeOfRecording - EVENT/SERIES
   */
  const editRecordingHandler = useCallback(
    (recordingInfo, typeOfRecording) => {
      showRecordingSettingHandler(recordingInfo, typeOfRecording);
    },
    [showRecordingSettingHandler]
  );

  // Reset the response data from editing a recording
  const resetEditRecordingResponse = useCallback(() => {
    dispatch(resetAction(EDIT_RECORDINGS, "content"));
  }, [dispatch]);

  const openCancelRecordingModal = useCallback(() => {
    const recordingInfo = selectedAssetRecordingInfo;
    const programDetails = selectedAssetRecordingInfo?.assetToRecord;
    const modalContent = {
      recordingInfo,
      programDetails,
      isRecordingRecorded: false,
      recordingType: RECORDING_PARAMS.EVENT,
      isMR,
      isRecordingNow: recordingInfo.isRecordingNow,
    };
    dispatch(showModalPopup(MODAL_TYPES.RECORDING, modalContent));
  }, [selectedAssetRecordingInfo, isMR, dispatch]);

  /**
   * Used by the VideoPlayer and Mini-EPG to determine what action should occur when a recording button is selected.
   * @param {Object} recordingInfo Recording information associated with the current airing program (if triggered from VideoPlayer) or the selected program (if triggered from the Mini-EPG)
   */
  const recordingButtonClickHandler = useCallback(
    (recordingInfo) => {
      if (recordingInfo) {
        setSelectedAssetRecordingInfo(recordingInfo);
        const seriesId = isMR ? recordingInfo.mediaRoom?.seriesId : recordingInfo.assetToRecord?.metadata?.seriesId;
        if (seriesId) {
          setShowRecordingModal(true);
        } else {
          if (isMR) {
            const recordingEventScheduledCheck = recordingInfo.recordingEventScheduledCheck;
            const recordingEventConflictCheck = recordingInfo.recordingEventConflictCheck ? true : false;
            const canScheduleRecording = !recordingEventScheduledCheck && !recordingEventConflictCheck ? true : false;
            if (canScheduleRecording) {
              recordingEventTypeRef.current = ANALYTICS_EVENT_TYPES.VIDEO_RECORD_START;
              trackGenericAction(
                ANALYTICS_EVENT_TYPES.VIDEO_RECORD_START,
                {
                  ...getMediaNodeMetaData(),
                  mappedContentType: MAPPED_CONTENT_TYPES.LPVR,
                },
                isDynatraceTrackingEnabled
              );
              // Null out ref immediately to prevent duplicate analytics in displayRecordingToast
              recordingEventTypeRef.current = null;
              setRecording(
                RECORDING_PARAMS.EVENT,
                appProvider,
                recordingInfo.assetToRecord,
                setRecordingAction,
                isMR,
                dispatch
              );
              logDTEvent(DT_EVENTS.RECORDING_START, null, isDynatraceTrackingEnabled);
            } else if (recordingEventConflictCheck) {
              setShowRecordingModal(true);
            } else {
              editRecordingHandler(recordingInfo, RECORDING_PARAMS.EVENT);
            }
          } else {
            const cpvrRecordingStatus = getCPVRRecordingStatus(recordingInfo);
            if (cpvrRecordingStatus === CPVR_INTERNAL_RECORDING_STATUS.EVENT_DOES_NOT_EXIST) {
              recordingEventTypeRef.current = ANALYTICS_EVENT_TYPES.VIDEO_RECORD_START;
              trackGenericAction(
                ANALYTICS_EVENT_TYPES.VIDEO_RECORD_START,
                {
                  ...getMediaNodeMetaData(),
                  mappedContentType: MAPPED_CONTENT_TYPES.CPVR,
                },
                isDynatraceTrackingEnabled
              );
              // Null out ref immediately to prevent duplicate analytics in displayRecordingToast
              recordingEventTypeRef.current = null;
              setRecording(
                RECORDING_PARAMS.EVENT,
                appProvider,
                recordingInfo.assetToRecord,
                setRecordingAction,
                isMR,
                dispatch
              );
              logDTEvent(DT_EVENTS.RECORDING_START, null, isDynatraceTrackingEnabled);
            } else {
              openCancelRecordingModal(recordingInfo, recordingInfo.assetToRecord);
            }
          }
        }
      }
    },
    [
      setSelectedAssetRecordingInfo,
      isMR,
      setShowRecordingModal,
      recordingEventTypeRef,
      getMediaNodeMetaData,
      appProvider,
      dispatch,
      editRecordingHandler,
      openCancelRecordingModal,
      isDynatraceTrackingEnabled,
    ]
  );

  const resetManipulatedRecordingParams = useCallback(() => {
    dispatch(resetAction(MANIPULATE_RECORDING_ACTION_TRIGGERED, "recordingParams"));
  }, [dispatch]);

  const resetSetRecordingResponse = useCallback(() => {
    dispatch(resetAction(SET_RECORDINGS, "content"));
  }, [dispatch]);

  const resetGetRecordingError = useCallback(() => {
    dispatch(resetAction(GET_RECORDINGS, "error"));
  }, [dispatch]);

  const resetDeleteRecordingResponse = useCallback(() => {
    dispatch(resetAction(DELETE_RECORDINGS, "content"));
  }, [dispatch]);

  // Reset the response data from setting a recording
  const resetSetTeamRecordingResponse = useCallback(() => {
    dispatch(resetAction(SET_TEAM_RECORDINGS, "content"));
  }, [dispatch]);

  /**
   * Resets the values used to support recording CRUD action flows
   */
  const resetRecordingActionValues = useCallback(() => {
    isRecordingActionInProgress.current = false;
    resetSetRecordingResponse();
    resetSetTeamRecordingResponse();
    resetDeleteRecordingResponse();
    resetEditRecordingResponse();
    resetManipulatedRecordingParams();
    resetGetRecordingError();
    setRecordingActionError(null);
  }, [
    isRecordingActionInProgress,
    resetSetRecordingResponse,
    resetSetTeamRecordingResponse,
    resetDeleteRecordingResponse,
    resetEditRecordingResponse,
    resetManipulatedRecordingParams,
    resetGetRecordingError,
    setRecordingActionError,
  ]);

  const closeRecordingModal = useCallback(() => {
    setShowRecordingModal(false);
  }, [setShowRecordingModal]);

  /**
   * Schedule a recording and save the response in redux
   *
   * @param {String} typeOfRecording - the recording type (event vs series)
   */
  const scheduleRecording = useCallback(
    (typeOfRecording) => {
      if (selectedAssetRecordingInfo) {
        recordingEventTypeRef.current = ANALYTICS_EVENT_TYPES.VIDEO_RECORD_START;
        setRecording(
          typeOfRecording,
          appProvider,
          selectedAssetRecordingInfo.assetToRecord,
          setRecordingAction,
          isMR,
          dispatch
        );
      }
    },
    [appProvider, dispatch, isMR, recordingEventTypeRef, selectedAssetRecordingInfo]
  );
  const getRecordingModal = useCallback(
    (isTeamSportEvent, scheduleTeamRecording) => {
      return (
        <RecordingModal
          closeModal={closeRecordingModal}
          recordingInfo={selectedAssetRecordingInfo}
          scheduleRecordingHandler={scheduleRecording}
          editRecordingHandler={editRecordingHandler}
          openCancelRecordingModal={openCancelRecordingModal}
          isSportsEventPage={isTeamSportEvent}
          scheduleTeamRecording={scheduleTeamRecording}
        />
      );
    },
    [closeRecordingModal, editRecordingHandler, openCancelRecordingModal, scheduleRecording, selectedAssetRecordingInfo]
  );

  return {
    recordingButtonClickHandler,
    openCancelRecordingModal,
    resetManipulatedRecordingParams,
    resetSetRecordingResponse,
    resetGetRecordingError,
    resetDeleteRecordingResponse,
    recordingEventTypeRef,
    resetRecordingActionValues,
    closeRecordingModal,
    getRecordingModal,
  };
};

export default useRecordingActions;
